import { formFieldType } from '@helpers/consts';
import { generateTransactionId } from '@helpers/generateTransactionId';
import {
  EnquiryFormData,
  Options,
  RegionSelectOption,
} from '@modules/EnquiryForm/EnquiryForm.types';
import { EnquiryForm } from './EnquiryTypeForm';
import { interpolateString } from '@helpers/stringHelpers';

const getAllFields = (formData: EnquiryFormData) => {
  return formData.FormSections.flatMap((section) => section.FormField);
};

export const getDefaultSelected = (
  options: RegionSelectOption[],
  isApplicationSelection: boolean
) => {
  if (isApplicationSelection) return 'select';

  // finds the first selected option
  const allGroups = options.reduce((acc, curr) => [...acc, ...curr.Options], [] as Options);
  const selected = allGroups.find((option) => option.Selected);

  return selected ? selected.Value : 'select';
};

export const getFormDefaultValues = (formData: EnquiryFormData) => {
  const defaultValues = {} as EnquiryForm;
  const allFields = getAllFields(formData);

  formData.FormSections.forEach((section) => {
    section.FormField.forEach((field) => {
      const dataType = field.DataType?.toLowerCase() || '';
      const name = field.Name;
      switch (dataType) {
        case formFieldType.quoteitem: {
          const namesArr = defaultValues.selectedProductNames
            ? [...defaultValues.selectedProductNames]
            : [];
          defaultValues.selectedProductNames = [...namesArr, name];
          break;
        }
        case formFieldType.regionselection:
        case formFieldType.industryselection:
        case formFieldType.productselection:
        case formFieldType.applicationselection:
          const isApplicationSelection = dataType === formFieldType.applicationselection;
          const options =
            field.IndustrySelectOptions || field.RegionSelectOptions || field.ProductSelectOptions;
          defaultValues[name] = getDefaultSelected(options, isApplicationSelection);
          break;
        case formFieldType.toggle:
          // if serial number is required, toggling 'spare parts' state by default
          const serialNrField = allFields.find((f) => f.Name === 'SerialNr');

          if (serialNrField && serialNrField.IsMandatory) {
            defaultValues[name] = true;
          }
          break;
        default:
          defaultValues[name] = undefined;
      }
    });
  });

  return defaultValues;
};

export const createFormData = (
  type: string,
  data: EnquiryForm,
  recaptchaToken: string,
  parentPage: {
    Id: string;
    Url: string;
  },
  i18n: Record<string, string>,
  ParentPageID
) => {
  const formDataPayload = new FormData();
  const { selectedProductNames, ...formData } = data;
  const selectedProductsString = data.selectedProductNames?.map((name) =>
    interpolateString(i18n.ProductSelectedMessage, name)
  );

  const formValues = {
    ...formData,

    recaptcha: recaptchaToken,
    Comments: (selectedProductsString ? selectedProductsString + ' ' : '') + formData.Comments,
    QuoteType: type,
    ContactUs: type,
    PageUrl: parentPage.Url,
    PageId: window.pageId,
    ParentPageId: parentPage.Id || ParentPageID,
    FormUrl: window.location.href,
    Lang: window.lang,
    transactionId: generateTransactionId(true),
  };

  Object.entries(formValues).forEach(([key, value]) => {
    switch (key) {
      case 'ImageUpload':
        const files = value as File[];

        files.forEach((file) => {
          formDataPayload.append('ImageUpload', file);
          formDataPayload.append('EmailAttachment', file);
        });
        break;
      case 'SerialNr':
        const serialNumbers = (value as string[]) || [];
        formDataPayload.append(key, serialNumbers.filter((value) => !!value).join(','));
        break;
      case 'Application':
        if (value === 'select') {
          formDataPayload.append(key, '');
        } else {
          formDataPayload.append(key, value?.toString() || '');
        }
        break;
      default:
        formDataPayload.append(key, value?.toString() || '');
    }
  });

  return { formDataPayload, formValues };
};
